import { useState, useEffect } from "react";

// Interface for date/time values
interface DateTime {
  departureDate: string; // Format: YYYY-MM-DD
  departureTime: string; // Format: HH:MM (24-hour)
}

// Hook to provide date and time 5 minutes in the future (Pacific Time)
// and auto-update if input time is now or in the past
const useDynamicDateTime = (
  selectedDate?: string, // Input date to validate
  selectedTime?: string // Input time to validate
): DateTime => {
  const [departureDate, setDepartureDate] = useState<string>("");
  const [departureTime, setDepartureTime] = useState<string>("");

  // Format date/time for Pacific Time Zone
  const formatDateTime = (date: Date): { date: string; time: string } => {
    const pacificOptions = { timeZone: "America/Los_Angeles" };
    const formattedDate = date
      .toLocaleString("en-CA", {
        ...pacificOptions,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      })
      .split(", ")[0]
      .split("/")
      .join("-");
    const formattedTime = date
      .toLocaleString("en-US", {
        ...pacificOptions,
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
      .replace(" ", "");
    return { date: formattedDate, time: formattedTime };
  };

  // Get date/time 5 minutes in the future
  const getFutureDateTime = (): { date: string; time: string } => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 5); // Add 5 minutes
    return formatDateTime(now);
  };

  // Check if input date/time is now or in the past
  const isPastOrCurrent = (date: string, time: string): boolean => {
    if (!date || !time) return true; // Treat empty inputs as past
    const inputDateTime = new Date(`${date}T${time}:00`);
    const now = new Date();
    return inputDateTime <= now;
  };

  // Initialize and update date/time
  useEffect(() => {
    // Set initial values to 5 minutes in the future
    const { date, time } = getFutureDateTime();
    setDepartureDate(date);
    setDepartureTime(time);

    // Check and update if selectedDate/selectedTime is past/current
    if (
      selectedDate &&
      selectedTime &&
      isPastOrCurrent(selectedDate, selectedTime)
    ) {
      setDepartureDate(date);
      setDepartureTime(time);
    }

    // Periodically check every 30 seconds
    const interval = setInterval(() => {
      if (
        selectedDate &&
        selectedTime &&
        isPastOrCurrent(selectedDate, selectedTime)
      ) {
        const { date: newDate, time: newTime } = getFutureDateTime();
        setDepartureDate(newDate);
        setDepartureTime(newTime);
      }
    }, 30000);

    return () => clearInterval(interval); // Cleanup interval
  }, [selectedDate, selectedTime]);

  return { departureDate, departureTime };
};

export default useDynamicDateTime;
